// Живые потоки: статус агента (общий) и события треда. EventSource сам переподключается
// и присылает Last-Event-ID — api докачивает пропущенное без дублей.
import { applyDelta, applyEvent, emptyThread, type ThreadEvent, type ThreadView } from '@autoaff/protocol';
import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import type { AgentStatus } from './api';

// ─── статус агента ──────────────────────────────────────────────────────
let status: AgentStatus | null = null;
let statusOnline = true;
const statusListeners = new Set<() => void>();
let statusSource: EventSource | null = null;

function startStatus(): void {
  if (statusSource) return;
  statusSource = new EventSource('/api/stream');
  statusSource.addEventListener('status', (e) => {
    status = JSON.parse((e as MessageEvent).data) as AgentStatus;
    statusOnline = true;
    for (const l of statusListeners) l();
  });
  statusSource.onerror = () => {
    statusOnline = false;
    for (const l of statusListeners) l();
  };
}

export function useAgentStatus(): { status: AgentStatus | null; online: boolean } {
  const snap = useSyncExternalStore(
    (cb) => {
      startStatus();
      statusListeners.add(cb);
      return () => statusListeners.delete(cb);
    },
    () => status,
  );
  return { status: snap, online: statusOnline };
}

// ─── тред ───────────────────────────────────────────────────────────────
export interface ThreadStream {
  view: ThreadView;
  ready: boolean;
  connected: boolean;
}

/** Состояние треда из живого потока. Перерисовка — не чаще одного раза за кадр. */
export function useThreadStream(threadId: string | null): ThreadStream {
  const viewRef = useRef<ThreadView>(emptyThread());
  const [, force] = useState(0);
  const [ready, setReady] = useState(false);
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    viewRef.current = emptyThread();
    setReady(false);
    force((x) => x + 1);
    if (!threadId) return;
    let frame = 0;
    const schedule = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        force((x) => x + 1);
      });
    };
    const es = new EventSource(`/api/threads/${threadId}/events`);
    es.onopen = () => setConnected(true);
    es.onerror = () => setConnected(false);
    es.addEventListener('ev', (e) => {
      applyEvent(viewRef.current, JSON.parse((e as MessageEvent).data) as ThreadEvent);
      schedule();
    });
    const onDelta = (e: Event) => {
      const d = JSON.parse((e as MessageEvent).data) as { runId: string; index: number; text: string };
      applyDelta(viewRef.current, d.runId, d.index, d.text);
      schedule();
    };
    es.addEventListener('delta', onDelta);
    es.addEventListener('partial', (e) => {
      const d = JSON.parse((e as MessageEvent).data) as { runId: string; index: number; text: string };
      viewRef.current.partial = { runId: d.runId, index: d.index, text: d.text };
      viewRef.current.working = true;
      schedule();
    });
    es.addEventListener('thinking', () => {
      viewRef.current.thinking = true;
      viewRef.current.working = true;
      schedule();
    });
    es.addEventListener('ready', () => {
      setReady(true);
      schedule();
    });
    return () => {
      es.close();
      if (frame) cancelAnimationFrame(frame);
    };
  }, [threadId]);

  return { view: viewRef.current, ready, connected };
}
